import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import HomePageVitrine from "./pages/Home/Home";
import LoginPage from "./pages/LoginPage/LoginPage";
import RegisterPage from "./pages/RegisterPage/RegisterPage";

import ResetPwd from "./pages/ResetPwd/ResetPwd";
import ForgotPasswordPage from "./pages/ResetPwd/ForgotPasswordPage";
import { UserProvider } from "./pages/Home/UserContext";
import PrimaryColorProvider from "./hooks/PrimaryColorProvider.jsx";
import { NotificationToastProvider } from "./components/Notification/NotificationManager.jsx";
import { SidebarProvider } from "./contexts/SidebarContext.js";

import Navbar3 from "./pages/Home/Navbar3/Navbar3";
import Footer from "./pages/Home/Footer";
import TopBarAdmin from "./components/TopBarAdmin/TopBarAdmin.jsx";

import { MessageProvider } from "./hooks/MessageContext.js";
import { ContactFormProvider } from "./hooks/ContactFormContext.js";
import { VoteProvider } from "./hooks/VoteContext";
import { ConvocationProvider } from "./hooks/ConvocationContext";

import ErrorPage from "./pages/ErrorPage/ErrorPage";

// Documents
import ConditionsUtilisationPage from "./pages/rgpd/ConditionsUtilisationPage.jsx";
import PolitiqueConfidentialitePage from "./pages/rgpd/PolitiqueConfidentialitePage.jsx";
import FaqPage from "./pages/rgpd/FaqPage.jsx";
import MentionsLegalesPage from "./pages/rgpd/MentionsLegalesPage.jsx";

// Pages Administrateurs
import AsideMenu from "./components/AsideMenu/AsideMenu";
import MainContent from "./components/MainContent/MainContent";

// Judo Vote NA
import Profile from "./pages/Profile/Profile";
import VotreClub from "./pages/PresencePro/VotreClub/VotreClub";
import PageCarteClubs from "./pages/PresencePro/PageCarteClubs/PageCarteClubs.jsx";
import MessagerieJudoka from "./pages/PresencePro/MessagerieJudoka/MessagerieJudoka.jsx";
import UserVotePage from "./pages/PresencePro/UserVotePage/UserVotePage.jsx";
import MonAppliCatePage from "./pages/MonAppliCatePage/MonAppliCatePage.jsx";
import TechniquesView from "./pages/PresencePro/TechniquesView/TechniquesView.jsx";
import TechniquesQuizz from "./pages/PresencePro/TechniquesQuizz/TechniquesQuizz.jsx";
import PartenairesKataPage from "./pages/PresencePro/PartenairesKataPage/PartenairesKataPage.jsx";
import CalendrierLigue from "./pages/PresencePro/CalendrierLigue/CalendrierLigue.jsx";
import ScanbarPage from "./pages/Scanbar/ScanbarPage.jsx";
import InscriptionPage from "./pages/InscriptionPage/InscriptionPage.jsx";
import ConvocationsPage from "./pages/ConvocationsPage/ConvocationsPage.jsx";

// Arbitrage
import ReglementArbitrage from "./pages/Arbitrage/ReglementArbitrage/ReglementArbitrage.jsx";
import QuizzVideoPage from "./pages/Arbitrage/QuizzVideoPage/QuizzVideoPage.jsx";
import FicheFraisPage from "./pages/FicheFrais/FicheFraisPage.jsx";

// VISITEURS
import CommissionsLigue from "./pages/CommissionsLigue/CommissionsLigue.jsx";
import InscriptionsEventsPage from "./pages/Public/InscriptionsEventsPage.jsx";
import NewsPage from "./pages/PresencePro/News/NewsPage.jsx";
import InfoCN from "./pages/PresencePro/InfoCN/InfoCN.jsx";
import PetitesAnnonces from "./pages/PresencePro/PetitesAnnonces/PetitesAnnonces.jsx";
import ResultsCalendrierLigue from "./pages/PresencePro/ResultsCalendrierLigue/ResultsCalendrierLigue.jsx";
import FormationDirigeants from "./pages/FormationDirigeants/FormationDirigeants.jsx";
import EventLivePublic from "./pages/Live/EventLivePublic.jsx";

// Pages Administrateurs
import CastingPage from "./pages/Casting/CastingPage.jsx";
import CastingScreenPage from "./pages/CastingScreen/CastingScreenPage";

import "./App.css";
import "./bulma.min.css";

const stripePromise = loadStripe(
  "pk_test_51POgHVEr14Icaey6JeVMxJf9bAJS9AxDAGUTSDI80b01LVDorrxErNHC1HbTU3WRgJfPD3P40daULyEzeakVDBfk00fHPS7sVp",
);

const RequireAuth = ({ children, roles }) => {
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  if (!userInfo) {
    return <div>Chargement...</div>;
  }

  if (roles && !roles.includes(userInfo.role_id)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

function AppContent() {
  const [userInfo, setUserInfo] = useState(null);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/appli");

  useEffect(() => {
    const getLocalStorageItem = (key) => {
      try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch (error) {
        console.error(
          "Erreur lors de la lecture du localStorage pour la clé",
          key,
          ":",
          error,
        );
        return null;
      }
    };

    const storedUserInfo = getLocalStorageItem("userInfo");
    if (storedUserInfo) {
      setUserInfo(storedUserInfo);
    } else {
      console.error(
        "Les informations de l'utilisateur stockées sont introuvables ou corrompues.",
      );
    }
  }, []);

  return (
    <Elements stripe={stripePromise}>
      <MessageProvider>
        <ContactFormProvider>
          <VoteProvider>
            <ConvocationProvider>
              <PrimaryColorProvider>
                <NotificationToastProvider>
                  <SidebarProvider>
                    <div className="flex min-h-screen flex-col bg-base-200 dark:bg-gray-600 dark:text-gray-200">
                      {isAdminRoute ? (
                        <TopBarAdmin />
                      ) : (
                        <Navbar3
                          user={{
                            nom: userInfo?.nom ?? "Chargement...",
                            prenom: userInfo?.prenom ?? "",
                            role_id: userInfo?.role_id ?? 0,
                            photoURL: userInfo?.photoURL ?? "",
                            userId: userInfo?.id ?? 0,
                          }}
                        />
                      )}

                      <Routes>
                        <Route path="/" element={<HomePageVitrine />} />

                        {/* Routes administratives protégées */}
                        <Route
                          path="/appli/*"
                          element={
                            <RequireAuth>
                              <div className="flex">
                                <AsideMenu />
                                <MainContent />
                              </div>
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/casting/:eventId"
                          element={
                            <RequireAuth roles={[1, 6, 7]}>
                              <CastingPage />
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/casting-screen/:eventId"
                          element={
                            <RequireAuth roles={[1, 6, 7]}>
                              <CastingScreenPage />
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/votre-club"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 6, 7, 8]}>
                              <VotreClub />
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/inscription-acteur"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 6, 7, 8]}>
                              <InscriptionPage />
                            </RequireAuth>
                          }
                        />

                        <Route path="/clubs-na" element={<PageCarteClubs />} />

                        <Route
                          path="/profile-utilisateur"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 6, 7, 8]}>
                              <Profile />
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/messagerie"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 6, 7, 8]}>
                              <MessagerieJudoka />
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/votes-utilisateur"
                          element={
                            <RequireAuth roles={[1, 2, 3, 4, 5, 6, 7, 8]}>
                              <UserVotePage />
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/reglement-arbitrage"
                          element={<ReglementArbitrage />}
                        />

                        <Route
                          path="/formation-dirigeants"
                          element={<FormationDirigeants />}
                        />

                        <Route
                          path="/quizz-arbitrage"
                          element={
                            <RequireAuth roles={[1, 4, 5]}>
                              <QuizzVideoPage />
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/mesconvocations"
                          element={
                            <RequireAuth roles={[1, 4, 5, 6, 7]}>
                              <ConvocationsPage />
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/fiche-de-frais"
                          element={
                            <RequireAuth roles={[1, 4, 5, 6, 7]}>
                              <FicheFraisPage />
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/commissions-ligue"
                          element={
                            <div className="flex">
                              <CommissionsLigue />
                            </div>
                          }
                        />

                        <Route
                          path="/petites-annonces"
                          element={<PetitesAnnonces />}
                        />

                        <Route
                          path="/scan-barcode"
                          element={
                            <RequireAuth roles={[1, 6, 7]}>
                              <ScanbarPage />
                            </RequireAuth>
                          }
                        />

                        <Route
                          path="/categories"
                          element={<MonAppliCatePage />}
                        />
                        <Route
                          path="/techniques-judo"
                          element={<TechniquesView />}
                        />
                        <Route
                          path="/techniques-quizz"
                          element={<TechniquesQuizz />}
                        />
                        <Route
                          path="/partenaires-kata"
                          element={<PartenairesKataPage />}
                        />
                        <Route
                          path="/calendrier-ligue"
                          element={<CalendrierLigue />}
                        />
                        <Route
                          path="/resultats-calendrier-ligue"
                          element={<ResultsCalendrierLigue />}
                        />
                        <Route
                          path="/inscriptions-events"
                          element={<InscriptionsEventsPage />}
                        />
                        <Route path="/news" element={<NewsPage />} />
                        <Route path="/infocn" element={<InfoCN />} />
                        <Route
                          path="/live-events"
                          element={<EventLivePublic />}
                        />
                        <Route
                          path="/live-events/:eventId"
                          element={<EventLivePublic />}
                        />

                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route
                          path="/reset-password/:token"
                          element={<ResetPwd />}
                        />
                        <Route
                          path="/forgot-password"
                          element={<ForgotPasswordPage />}
                        />
                        <Route path="/404" element={<ErrorPage />} />
                        <Route path="*" element={<ErrorPage />} />
                        <Route
                          path="/conditions-utilisation"
                          element={<ConditionsUtilisationPage />}
                        />
                        <Route
                          path="/politique-confidentialite"
                          element={<PolitiqueConfidentialitePage />}
                        />
                        <Route path="/faq" element={<FaqPage />} />
                        <Route
                          path="/mentions-legales"
                          element={<MentionsLegalesPage />}
                        />
                      </Routes>

                      {!isAdminRoute && <Footer />}
                    </div>
                  </SidebarProvider>
                </NotificationToastProvider>
              </PrimaryColorProvider>
            </ConvocationProvider>
          </VoteProvider>
        </ContactFormProvider>
      </MessageProvider>
    </Elements>
  );
}

const App = () => {
  return (
    <UserProvider>
      <Router>
        <AppContent />
      </Router>
    </UserProvider>
  );
};

export default App;
